import { NormalText } from "@/components/text/NormalText";
import { ConvertButtonStep } from "@/components/tutorial/tutorialSteps/ConvertButtonStep";
import { InputStep } from "@/components/tutorial/tutorialSteps/InputStep";
import { OpenArtistModalStep } from "@/components/tutorial/tutorialSteps/OpenArtistModalStep";
import { OutputStep } from "@/components/tutorial/tutorialSteps/OutputStep";
import { SubmittingStep } from "@/components/tutorial/tutorialSteps/SubmittingStep";
import { getConversionInputNamespace } from "@/constants/namespaces";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { loadFile } from "@/lib/zustand/useTutorial/loadFile";
import {
  ConversionConstructed,
  TutorialSample,
  TutorialStep,
} from "@/types/types";

export const getTutorialSteps = (
  tutorialSample: TutorialSample,
  conversionId: ConversionConstructed["id"]
): TutorialStep[] => {
  const isPitchShift = tutorialSample.pitchShift !== 0;

  return [
    {
      targetSelector: "[data-tutorial='conversion-input']",
      content: <InputStep />,
      nextOnHoleClick: true,
      showNextButton: false,
      clickTargetOnNext: false,
      // If the user doesn't actually select a sample we set the sample to the default one
      onNextClick: async () => {
        const file = await loadFile(tutorialSample.inputFile);
        const setInputFile = useAudioInput
          .getState()
          .getNamedStore(getConversionInputNamespace(0))
          .getState().setInputFile;

        await setInputFile({ file, isFirstFile: false });
      },
    },
    {
      targetSelector: "[data-tutorial='artist-selection-button']",
      content: <OpenArtistModalStep />,
      nextOnHoleClick: true,
      showNextButton: true,
      clickTargetOnNext: true,
      onNextClick: null,
    },
    {
      targetSelector: `[data-tutorial="genre-filter-${tutorialSample.genre}"]`,
      content: (
        <NormalText elementType="span">
          Let's filter by {tutorialSample.genre}
        </NormalText>
      ),
      nextOnHoleClick: true,
      showNextButton: true,
      clickTargetOnNext: true,
      onNextClick: null,
    },
    {
      targetSelector: `[data-tutorial="artist-${tutorialSample.artist.id}"]`,
      content: (
        <NormalText elementType="span">Let's select this artist!</NormalText>
      ),
      nextOnHoleClick: true,
      showNextButton: true,
      clickTargetOnNext: true,
      onNextClick: null,
    },
    // If there isn't any actual pitch shift, we don't need to show the pitch shift steps
    ...(isPitchShift ?
      [
        {
          targetSelector: `[data-tutorial="pitch-shift"]`,
          content: (
            <NormalText elementType="span">
              Let's pitch shift by {tutorialSample.pitchShift} to better fit the
              new singer's range
            </NormalText>
          ),
          nextOnHoleClick: true,
          showNextButton: true,
          clickTargetOnNext: false,
          onNextClick: () => {
            // Adjust pitch shift
            const setPitch = useConversionHyperParameters.getState().setPitch;
            setPitch(tutorialSample.pitchShift);
          },
        },
        {
          targetSelector: `[data-tutorial="pitch-shift"]`,
          content: (
            <NormalText elementType="span" className="text-green-600">
              ✓ Adjusted pitch shift
            </NormalText>
          ),
          nextOnHoleClick: true,
          showNextButton: true,
          clickTargetOnNext: false,
          onNextClick: null,
        },
      ]
    : []),
    {
      targetSelector: "[data-tutorial='convert-button']",
      content: <ConvertButtonStep />,
      nextOnHoleClick: true,
      showNextButton: true,
      clickTargetOnNext: true,
      onNextClick: null,
    },
    {
      targetSelector: "[data-tutorial='convert-button-submitting']",
      content: <SubmittingStep />,
      nextOnHoleClick: false,
      showNextButton: false,
      clickTargetOnNext: false,
      onNextClick: null,
    },
    {
      targetSelector: `[data-tutorial='conversion-${conversionId}']`,
      content: <OutputStep />,
      nextOnHoleClick: true,
      showNextButton: true,
      clickTargetOnNext: false,
      onNextClick: null,
    },
  ];
};
